import React from "react";
import { useQuery } from "@tanstack/react-query";
import { useForm, Controller } from "react-hook-form";
import Container from "../../componentes/Container";
import { Cartao, BotaoComTextoEIcone } from "@digix-ui/componentes";
import { CampoDeTexto } from "../../componentes";
import { buscarCidades } from "../LocaisAtendimento/services/buscarCidades";
import "./styles.scss";

type FormPeriodo = { inicio: string; fim: string };
type FormCidade = { cidadeId: string };

const RelatoriosInscricoes: React.FC = () => {
  const { data: cidades, isLoading: loadingCidades } = useQuery({
    queryKey: ["cidades"],
    queryFn: buscarCidades,
  });

  const {
    control: ctrlPeriodo,
    handleSubmit: handlePeriodo,
    watch: watchPeriodo,
  } = useForm<FormPeriodo>({
    defaultValues: { inicio: "", fim: "" },
    mode: "onChange",
  });
  const periodo = watchPeriodo();
  const podeGerarQuantidade =
    periodo.inicio?.length === 10 && periodo.fim?.length === 10;

  const {
    control: ctrlPretendentes,
    handleSubmit: handlePretendentes,
    watch: watchPret,
  } = useForm<FormCidade>({
    defaultValues: { cidadeId: "" },
    mode: "onChange",
  });
  const podeGerarPret = Boolean(watchPret("cidadeId"));

  const {
    control: ctrlDetalhada,
    handleSubmit: handleDetalhada,
    watch: watchDet,
  } = useForm<FormCidade>({
    defaultValues: { cidadeId: "" },
    mode: "onChange",
  });
  const podeGerarDet = Boolean(watchDet("cidadeId"));

  const {
    control: ctrlTotal,
    handleSubmit: handleTotal,
    watch: watchTot,
  } = useForm<FormCidade>({
    defaultValues: { cidadeId: "" },
    mode: "onChange",
  });
  const podeGerarTot = Boolean(watchTot("cidadeId"));

  const gerarQuantidade = ({ inicio, fim }: FormPeriodo) => {
    const qs = new URLSearchParams();
    qs.set("inicio", inicio);
    qs.set("fim", fim);
    window.open(
      `/relatorios/quantidade-de-inscritos?${qs.toString()}`,
      "_blank"
    );
  };
  const gerarPretendentes = ({ cidadeId }: FormCidade) => {
    const qs = new URLSearchParams();
    qs.set("idCidade", cidadeId);
    window.open(`/relatorios/lista-de-pretendentes?${qs.toString()}`, "_blank");
  };
  const gerarDetalhada = ({ cidadeId }: FormCidade) => {
    const qs = new URLSearchParams();
    qs.set("idCidade", cidadeId);
    window.open(`/relatorios/lista-detalhada?${qs.toString()}`, "_blank");
  };
  const gerarTot = ({ cidadeId }: FormCidade) => {
    const qs = new URLSearchParams();
    qs.set("idCidade", cidadeId);
    window.open(`/relatorios/total-de-inscritos?${qs.toString()}`, "_blank");
  };

  const mascaraDataBR = (valor: string) => {
    const v = valor.replace(/\D/g, "").slice(0, 8);
    const dia = v.slice(0, 2);
    const mes = v.slice(2, 4);
    const ano = v.slice(4, 8);

    if (v.length <= 2) return dia;
    if (v.length <= 4) return `${dia}/${mes}`;
    return `${dia}/${mes}/${ano}`;
  };

  return (
    <Container comFundo tamanhoDoContainer="grande" comCabecalhoFixo>
      <div className="relatorios-grid">
        <Cartao
          cor="secundaria"
          icone="fal fa-clipboard-list"
          titulo={
            <span className="u-texto-negrito">Quantidade de inscritos</span>
          }
        >
          <p className="relatorio-descricao">
            Esse relatório informa quantas inscrições foram realizadas,
            separadas por cidade e em determinado período. Além disso, as
            categoriza pelo status em que se encontram.
          </p>

          <form
            className="relatorio-form"
            onSubmit={handlePeriodo(gerarQuantidade)}
          >
            <div className="campo" style={{ gridColumn: "1 / -1" }}>
              <label className="campo__rotulo">Defina um período</label>
              <div className="relatorio-form__periodo">
                <Controller
                  name="inicio"
                  control={ctrlPeriodo}
                  rules={{ required: true }}
                  render={({ field }) => (
                    <CampoDeTexto
                      {...field}
                      tipo="text"
                      placeholder="DD/MM/AAAA"
                      maxLength={10}
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                        field.onChange(mascaraDataBR(e.target.value))
                      }
                    />
                  )}
                />
                <span className="relatorio-form__ate">até</span>
                <Controller
                  name="fim"
                  control={ctrlPeriodo}
                  rules={{ required: true }}
                  render={({ field }) => (
                    <CampoDeTexto
                      {...field}
                      tipo="text"
                      placeholder="DD/MM/AAAA"
                      maxLength={10}
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                        field.onChange(mascaraDataBR(e.target.value))
                      }
                    />
                  )}
                />
              </div>
            </div>

            <div className="relatorio-form__acoes">
              <BotaoComTextoEIcone
                tipo="submit"
                cor="secundaria"
                tamanho="pequeno"
                icone="far fa-file-alt"
                posicaoDoIcone="esquerda"
                habilitado={podeGerarQuantidade}
                classesAuxiliares={`botao-relatorio ${
                  podeGerarQuantidade ? "" : "is-desabilitado"
                }`}
              >
                Gerar relatório
              </BotaoComTextoEIcone>
            </div>
          </form>
        </Cartao>

        <Cartao
          cor="secundaria"
          icone="fal fa-user-check"
          titulo={
            <span className="u-texto-negrito">
              Lista de pretendentes inscritos
            </span>
          }
        >
          <p className="relatorio-descricao">
            Esse relatório informa quais são os pretendentes inscritos,
            separados por cidade. Informa seus dados cadastrais e as datas de
            inscrição e atualização.
          </p>

          <form
            className="relatorio-form"
            onSubmit={handlePretendentes(gerarPretendentes)}
          >
            <div className="campo">
              <label className="campo__rotulo">Selecione uma cidade</label>
              <Controller
                name="cidadeId"
                control={ctrlPretendentes}
                rules={{ required: true }}
                render={({ field }) => (
                  <select
                    {...field}
                    className="campo__entrada"
                    disabled={loadingCidades}
                  >
                    <option value="">Selecionar</option>
                    {cidades?.map((c: any) => (
                      <option key={c.id} value={c.id}>
                        {c.nome}
                      </option>
                    ))}
                  </select>
                )}
              />
            </div>

            <div className="relatorio-form__acoes">
              <BotaoComTextoEIcone
                tipo="submit"
                cor="secundaria"
                tamanho="pequeno"
                icone="far fa-file-alt"
                posicaoDoIcone="esquerda"
                habilitado={podeGerarPret}
                classesAuxiliares={`botao-relatorio ${
                  podeGerarPret ? "" : "is-desabilitado"
                }`}
              >
                Gerar relatório
              </BotaoComTextoEIcone>
            </div>
          </form>
        </Cartao>

        <Cartao
          cor="secundaria"
          icone="fal fa-list-alt"
          titulo={
            <span className="u-texto-negrito">
              Lista de inscritos detalhada
            </span>
          }
        >
          <p className="relatorio-descricao">
            Esse relatório é uma versão mais detalhada sobre quais são os
            inscritos, separados por cidade. Informa seus dados cadastrais, os
            dados do cônjuge, dados de documentos, datas de inscrição, moradia,
            inscrição em projetos habitacionais.
          </p>

          <form
            className="relatorio-form"
            onSubmit={handleDetalhada(gerarDetalhada)}
          >
            <div className="campo">
              <label className="campo__rotulo">Selecione uma cidade</label>
              <Controller
                name="cidadeId"
                control={ctrlDetalhada}
                rules={{ required: true }}
                render={({ field }) => (
                  <select
                    {...field}
                    className="campo__entrada"
                    disabled={loadingCidades}
                  >
                    <option value="">Selecionar</option>
                    {cidades?.map((c: any) => (
                      <option key={c.id} value={c.id}>
                        {c.nome}
                      </option>
                    ))}
                  </select>
                )}
              />
            </div>

            <div className="relatorio-form__acoes">
              <BotaoComTextoEIcone
                tipo="submit"
                cor="secundaria"
                tamanho="pequeno"
                icone="far fa-file-alt"
                posicaoDoIcone="esquerda"
                habilitado={podeGerarDet}
                classesAuxiliares={`botao-relatorio ${
                  podeGerarDet ? "" : "is-desabilitado"
                }`}
              >
                Gerar relatório
              </BotaoComTextoEIcone>
            </div>
          </form>
        </Cartao>

        <Cartao
          cor="secundaria"
          icone="fal fa-chart-pie"
          titulo={<span className="u-texto-negrito">Total de inscritos</span>}
        >
          <p className="relatorio-descricao">
            Esse relatório é uma versão mais detalhada sobre quais são os
            inscritos, separados por cidade. Informa seus dados cadastrais, os
            dados do cônjuge, dados de documentos, datas de inscrição, moradia,
            inscrição em projetos habitacionais.
          </p>

          <form className="relatorio-form" onSubmit={handleTotal(gerarTot)}>
            <div className="campo">
              <label className="campo__rotulo">Selecione uma cidade</label>
              <Controller
                name="cidadeId"
                control={ctrlTotal}
                rules={{ required: true }}
                render={({ field }) => (
                  <select
                    {...field}
                    className="campo__entrada"
                    disabled={loadingCidades}
                  >
                    <option value="">Selecionar</option>
                    {cidades?.map((c: any) => (
                      <option key={c.id} value={c.id}>
                        {c.nome}
                      </option>
                    ))}
                  </select>
                )}
              />
            </div>

            <div className="relatorio-form__acoes">
              <BotaoComTextoEIcone
                tipo="submit"
                cor="secundaria"
                tamanho="pequeno"
                icone="far fa-file-alt"
                posicaoDoIcone="esquerda"
                habilitado={podeGerarTot}
                classesAuxiliares={`botao-relatorio ${
                  podeGerarTot ? "" : "is-desabilitado"
                }`}
              >
                Gerar relatório
              </BotaoComTextoEIcone>
            </div>
          </form>
        </Cartao>
      </div>
    </Container>
  );
};

export default RelatoriosInscricoes;
